import { alpha, createTheme, darken, getContrastRatio, lighten } from '@mui/material/styles';
import themeFile from '../theme.json';

const isHex = (value) => typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value.trim());

export const parseReportColors = (value) => {
  try {
    const colors = typeof value === 'string' ? JSON.parse(value) : value;
    return colors && typeof colors === 'object' ? colors : {};
  } catch (error) {
    return {};
  }
};

export const createReportBrandTheme = (value) => {
  const colors = parseReportColors(value);
  const primary = isHex(colors?.primary?.main) ? colors.primary.main.trim() : themeFile.palette.primary.main;
  const secondary = isHex(colors?.secondary?.main) ? colors.secondary.main.trim() : themeFile.palette.secondary.main;
  const contrastText = (color) => getContrastRatio(color, '#ffffff') >= 4.5 ? '#ffffff' : '#111827';

  const controlText = getContrastRatio(primary, '#ffffff') >= 4.5 ? primary : darken(primary, 0.65);

  const sidebar = {
    background: '#ffffff',
    selected: controlText,
    selectedText: contrastText(controlText),
    contact: lighten(secondary, 0.94),
    text: '#334155',
    accent: getContrastRatio(primary, '#ffffff') >= 3 ? primary : darken(primary, 0.5),
    border: '#E2E8F0',
  };

  return createTheme({
    ...themeFile,
    palette: {
      ...themeFile.palette,
      sidebar,
      primary: { main: primary, contrastText: contrastText(primary) },
      secondary: { main: secondary, contrastText: contrastText(secondary) },
      divider: alpha(primary, 0.16),
    },
    components: {
      ...themeFile.components,
      MuiLinearProgress: {
        styleOverrides: {
          root: { height: 6, borderRadius: 999, backgroundColor: alpha(primary, 0.1) },
          bar: { borderRadius: 999, backgroundImage: `linear-gradient(90deg, ${primary} 0%, ${secondary} 100%)` },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: sidebar.background,
            borderRight: `1px solid ${sidebar.border}`,
            boxShadow: 'none',
            overflow: 'hidden',
            color: sidebar.text,
            '& .MuiListSubheader-root': {
              backgroundColor: lighten(primary, 0.95),
              color: sidebar.text,
              borderRadius: 6,
              minWidth: '0 !important',
              margin: '12px 12px 4px',
              paddingInline: 12,
              maxHeight: 32,
              '&:first-child': { marginTop: 4 },
              boxSizing: 'border-box',
              lineHeight: '32px',
              fontWeight: 700,
              letterSpacing: '0.04em',
            },
            '& nav .MuiList-root': { padding: '8px 0' },
            '& nav .MuiListItem-root': { paddingInline: 12 },
            '& nav:has(.MuiListItemButton-root .MuiTypography-caption) .MuiListSubheader-root': { margin: 0 },
            '& nav .MuiListItemText-root': { margin: 0 },
            '& nav .MuiListItemText-primary': { fontWeight: 600, fontSize: '0.8125rem', color: 'inherit' },
            '& nav .MuiListItemButton-root': {
              color: sidebar.text,
              borderRadius: 6,
              border: '1px solid transparent',
              minHeight: 44, height: 44,
              padding: '8px 12px',
              margin: '2px 0',
              transition: 'background-color 0.15s ease, color 0.15s ease',
              '&:hover': { backgroundColor: lighten(primary, 0.94) },
              '&:focus-visible': { outline: `2px solid ${sidebar.accent}`, outlineOffset: -2 },
              '@media (pointer: coarse)': { minHeight: 44, height: 44 },
              '&:has(.MuiTypography-caption)': { height: 48, minHeight: 48, '& .MuiListItemIcon-root': { marginRight: 0 } },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            },
            '& nav .MuiListItemButton-root.Mui-selected': {
              backgroundColor: sidebar.selected,
              color: sidebar.selectedText,
              borderColor: 'transparent',
              '&:hover': { backgroundColor: darken(sidebar.selected, 0.12) },
              '&:focus-visible': { outlineColor: sidebar.accent },
            },
            '& nav .MuiListItemIcon-root': {
              minWidth: 24, width: 24, height: 24, display: 'grid', placeItems: 'center', color: 'inherit', marginRight: 10,
              '& svg': { color: 'inherit !important', width: 20, height: 20 },
            },
            '& nav .MuiListItemButton-root.Mui-selected .MuiListItemIcon-root': { color: sidebar.selectedText },
            '& nav .MuiListItemButton-root.Mui-selected .MuiTypography-root': { color: 'inherit', fontWeight: 700 },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: alpha(primary, 0.12),
              color: '#111827',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: { zIndex: '0!important', backgroundColor: '#ffffff' },
          root: ({ theme }) => ({ padding: theme.spacing(0.25) }),
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: { '&.Mui-active': { color: primary } },
          icon: { color: `${secondary} !important` },
        },
      },
      MuiListItemButton: {
        styleOverrides: { root: { '&.Mui-selected': { backgroundColor: alpha(primary, 0.12) } } },
      },
      MuiDialog: {
        styleOverrides: { paper: {
          backgroundColor: '#ffffff !important', backgroundImage: 'none !important', color: '#0F172A',
          backdropFilter: 'none', border: '1px solid #E2E8F0', borderRadius: 12,
          '& .MuiOutlinedInput-root': { background: '#ffffff', boxShadow: 'none', color: '#0F172A' },
          '& .MuiInputLabel-root': { color: '#475569' },
          '& .MuiDialogContent-root > .MuiBox-root, & .MuiFormControl-root': { minWidth: 0, maxWidth: '100%' },
          '& .MuiFormLabel-root.Mui-focused': { color: controlText },
        } },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { background: '#ffffff !important', color: '#0F172A !important', borderBottom: '1px solid #E2E8F0', padding: '20px 24px', fontWeight: 700, fontSize: '1.2rem', marginBottom: 0 } },
      },
      MuiDialogContent: { styleOverrides: { root: { padding: 24, '&.MuiDialogContent-root': { paddingTop: 24 } } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '16px 24px', gap: 8, borderTop: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', '& .MuiButton-root': { minHeight: 44 }, '& .MuiButton-root:not(:last-child)': { background: 'transparent', color: controlText, boxShadow: 'none' } } } },
      MuiBackdrop: { styleOverrides: { root: { backgroundColor: 'rgba(15, 23, 42, 0.48)' } } },
      MuiInputLabel: { styleOverrides: { root: { color: '#475569', '&.Mui-focused': { color: controlText } }, shrink: { color: '#475569' } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: primary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primary },
          },
          notchedOutline: { borderColor: alpha(primary, 0.38) },
        },
      },
    },
  });
};
